import React from "react";
import { ArrowRight, ShieldCheck, CalendarCheck2, HardHat, Layers, MapPin, Clock, PhoneCall, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const contractorNeeds = [{
  title: "Aligned with roofing schedules",
  description: "Access built ready before your stripping or slating crews arrive on site."
}, {
  title: "Practical roof-level working access",
  description: "Lifts and loading bays configured specifically for ridge, eaves, and tile handling."
}, {
  title: "Continuous edge protection",
  description: "Guardrails, toeboards, and debris netting ensuring full Working at Height compliance."
}, {
  title: "Temporary roof options",
  description: "Corrugated and tin-hat weather protection systems for complete structural refits."
}, {
  title: "Clear coordination when dates shift",
  description: "Direct contact with supervisors when weather or supplier delays alter your timeline."
}];
const relevantServices = [{
  title: "Roofing Scaffolding",
  description: "Progressive lifts, hop-up brackets, and safe loading platforms for re-roofs.",
  to: "/RoofingScaffoldingPage",
  icon: HardHat
}, {
  title: "Temporary Roofs",
  description: "Complete weatherproofing canopies to keep properties dry throughout structural works.",
  to: "/TemporaryRoofsPage",
  icon: Layers
}, {
  title: "Edge Protection",
  description: "Robust perimeter handrails and hand-over certified edge barriers for pitched roofs.",
  to: "/EdgeProtectionPage",
  icon: ShieldCheck
}, {
  title: "Chimney Scaffolding",
  description: "Saddle scaffolds and ridge-straddling towers for stack repairs and repointing.",
  to: "/ChimneyScaffoldingPage",
  icon: CalendarCheck2
}];
const coverageAreas = ["Berkshire", "Surrey", "Hampshire", "Buckinghamshire", "London", "South East"];
const accreditations = ["NASC Member", "CHAS Accredited"];
export default function RoofingHero() {
  return <section id="roofingcontractorshero" data-nav="light" className="relative bg-background text-foreground pt-8 pb-16 md:pt-12 md:pb-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Badges */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <Badge variant="outline" className="border-border text-foreground font-medium px-3 py-1">
            Contractor Trade Partner
          </Badge>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-1">
            <MapPin className="size-3.5 text-primary" />
            <span>Regional Coverage: South East & Home Counties</span>
          </div>
        </div>

        {/* Main Grid: Headline & Core Philosophy */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          <div className="lg:col-span-7">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6 font-default">
              Scaffolding for Roofing Contractors
            </h1>
            
            <p className="text-lg text-muted-foreground leading-relaxed mb-6 font-sans">
              Roofing projects run on tight weather windows and strict handover milestones. 
              As a roofing contractor, you need scaffold ready in line with your programme, 
              practical working access at eaves and ridge heights, fully compliant edge protection, 
              weatherproof temporary roof options, and reliable communication whenever site 
              conditions change.
            </p>

            <div className="rounded-xl border border-border bg-card p-6 shadow-sm mb-8">
              <div className="flex items-start gap-4">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                  <CheckCircle2 className="size-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-card-foreground mb-1 font-default">
                    Our partnership approach
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    BMF Scaffolding Ltd supports trade teams with planned access aligned directly to 
                    roofing schedules, responsive site coordination, and transparent commercial contractor pricing. 
                    BMF aims to become the scaffolding partner roofing contractors can rely on job after job.
                  </p>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactRequestQuotePage">
                  <span>Discuss your roofing project</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/AreasWeCoverPage">
                  <span>View coverage map</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Contractor Requirements Checklist */}
          <div className="lg:col-span-5">
            <Card className="border-border shadow-md bg-card">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold font-default text-card-foreground">
                    What roofing contractors need
                  </CardTitle>
                  <Clock className="size-4 text-muted-foreground" />
                </div>
                <CardDescription className="text-xs text-muted-foreground">
                  The standards we build into every trade contractor handover
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                {contractorNeeds.map((need, i) => <div key={i} data-index={i} className="flex items-start gap-3 text-sm">
                    <div className="size-5 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-xs font-semibold">{i + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground leading-snug">
                        {need.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                        {need.description}
                      </p>
                    </div>
                  </div>)}

                <Separator className="my-3" />

                {/* Accreditations strip */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Verified Accreditations
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {accreditations.map((acc, accIdx) => <Badge key={accIdx} data-index={accIdx} variant="secondary" className="text-xs font-normal">
                        <ShieldCheck className="size-3 text-primary mr-1" />
                        {acc}
                      </Badge>)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Relevant Scaffolding Services Section */}
        <div className="pt-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 pb-4 border-b border-border gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">
                Specialist Solutions
              </p>
              <h2 className="text-2xl font-bold tracking-tight text-foreground font-default">
                Relevant services for roofing teams
              </h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Designed around pitched roofs, flat decks, tile loading, and chimney access requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {relevantServices.map((service, idx) => {
            const IconComponent = service.icon;
            return <div key={idx} data-index={idx}>
                  <Card className="h-full flex flex-col justify-between border-border hover:border-primary/40 transition-[border-color,box-shadow] duration-200 shadow-xs hover:shadow-md bg-card">
                    <CardHeader className="pb-3">
                      <div className="p-3 rounded-lg bg-muted text-foreground w-fit mb-3">
                        <IconComponent className="size-5 text-primary" />
                      </div>
                      <CardTitle className="text-base font-semibold text-card-foreground font-default">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {service.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0 pb-6">
                      <Button asChild variant="link" className="px-0 h-auto text-xs font-semibold">
                        <Link to={service.to}>
                          <span>Explore specifications</span>
                          <ArrowRight className="size-3.5 ml-1" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        {/* Geographic Coverage Footnote */}
        <div className="mt-12 rounded-xl bg-card border border-border p-6 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <MapPin className="size-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground font-default">
                  Geographic Coverage
                </h3>
              </div>
              <p className="text-xs text-muted-foreground">
                Active teams operating daily across local boroughs and contractor supply routes.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {coverageAreas.map((area, areaIdx) => <span key={areaIdx} data-index={areaIdx} className="inline-flex items-center text-xs font-medium px-3 py-1 rounded-md bg-muted text-muted-foreground">
                  {area}
                </span>)}
            </div>

            <div className="shrink-0">
              <Button asChild variant="outline" size="sm">
                <Link to="/ContactRequestQuotePage">
                  <PhoneCall className="size-3.5 mr-1 text-primary" />
                  <span>Request trade pricing</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}